interface RecoveryQueueOptions {
  /** Upper bound on dispatches in flight; below 1 still runs one at a time. */
  concurrency: number;
}

/**
 * Runs `dispatch` over `items` with at most `concurrency` in flight, each worker
 * pulling the next item as it frees up. Results are positionally aligned with
 * `items` and never reject, exactly like `Promise.allSettled`.
 */
export const runRecoveryQueue = async <T, R>(
  items: T[],
  dispatch: (item: T) => Promise<R>,
  { concurrency }: RecoveryQueueOptions
): Promise<PromiseSettledResult<R>[]> => {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  // One shared cursor, so a slow item costs its own worker and no other.
  const queue = items.entries();

  const worker = async (): Promise<void> => {
    for (const [index, item] of queue) {
      try {
        results[index] = { status: 'fulfilled', value: await dispatch(item) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  const workerCount = Math.min(Math.max(concurrency, 1), items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
};
